import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Package, Search, CheckCircle2, Circle, Loader2, Truck } from "lucide-react";
import { toast } from "sonner";
import { api, money } from "../lib/api";
import { PageHero } from "../components/FormKit";

const STAGES = ["Order Placed", "Processing", "Preparing Your Order", "Shipped", "Delivered"];

function stageIndex(status) {
  const s = (status || "").toLowerCase();
  if (s.includes("deliver")) return 4;
  if (s.includes("ship")) return 3;
  if (s.includes("preparing")) return 2;
  if (s.includes("process")) return 1;
  return 1;
}

export default function TrackingCenter() {
  const [orderNumber, setOrderNumber] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError(""); setOrder(null);
    try {
      const { data } = await api.post("/track", { order_number: orderNumber, email });
      setOrder(data.order);
    } catch (err) {
      const msg = err.response?.data?.detail || "Unable to track this order right now.";
      setError(msg);
      toast.error("Order not found");
    } finally { setLoading(false); }
  };

  const active = order ? stageIndex(order.status) : -1;

  return (
    <div data-testid="tracking-page">
      <PageHero eyebrow="Order Tracking" title="TAVEN Tracking Center" subtitle="Enter your order number and email to see the latest status of your delivery." />
      <section className="py-10 px-4 md:px-8 mx-auto max-w-3xl">
        <form onSubmit={submit} className="bg-white border border-black/10 p-6 grid sm:grid-cols-[1fr_1fr_auto] gap-3 items-end">
          <div>
            <label className="text-xs font-bold uppercase tracking-wider block mb-1.5">Order Number</label>
            <input required value={orderNumber} onChange={(e) => setOrderNumber(e.target.value)} placeholder="e.g. 1001" data-testid="track-order-number" className="w-full border border-black/15 focus:border-taven-gold px-4 py-3 text-sm outline-none" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider block mb-1.5">Email</label>
            <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email used at checkout" data-testid="track-email" className="w-full border border-black/15 focus:border-taven-gold px-4 py-3 text-sm outline-none" />
          </div>
          <button disabled={loading} data-testid="track-submit" className="bg-taven-black text-white font-bold uppercase tracking-wider py-3 px-6 hover:bg-taven-orange transition-colors disabled:opacity-60 flex items-center justify-center gap-2 h-[46px]">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Track
          </button>
        </form>

        {error && <div className="mt-6 p-5 bg-taven-gray/50 border border-black/10 text-sm text-muted-foreground" data-testid="track-error">{error}</div>}

        {order && (
          <div className="mt-6 bg-white border border-black/10 p-6" data-testid="track-result">
            <div className="flex items-center justify-between flex-wrap gap-2 border-b border-black/10 pb-4">
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground">Order</p>
                <p className="font-black font-display text-lg">{order.name || `#${order.order_number}`}</p>
              </div>
              <div className="text-right">
                <p className="text-xs uppercase tracking-wider text-muted-foreground">Status</p>
                <p className="font-bold text-taven-orange flex items-center gap-1.5"><Truck className="w-4 h-4" /> {order.status}</p>
              </div>
            </div>

            <div className="flex justify-between mt-8 mb-2 relative">
              {STAGES.map((s, i) => (
                <div key={s} className="flex flex-col items-center text-center flex-1 relative z-10">
                  {i <= active ? <CheckCircle2 className="w-7 h-7 text-taven-orange fill-taven-orange/10" /> : <Circle className="w-7 h-7 text-black/20" />}
                  <span className={`text-[10px] font-bold uppercase tracking-wider mt-2 ${i <= active ? "text-black" : "text-muted-foreground"}`}>{s}</span>
                </div>
              ))}
              <div className="absolute top-3.5 left-[10%] right-[10%] h-0.5 bg-black/10 -z-0">
                <div className="h-0.5 bg-taven-orange transition-all" style={{ width: `${(active / (STAGES.length - 1)) * 100}%` }} />
              </div>
            </div>

            {order.items?.length > 0 && (
              <div className="mt-8 border-t border-black/10 pt-4">
                <p className="text-xs font-bold uppercase tracking-[0.2em] mb-3">Items</p>
                <div className="space-y-3">
                  {order.items.map((it, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-taven-gray overflow-hidden shrink-0">{it.image && <img src={it.image} alt={it.title} className="w-full h-full object-cover" />}</div>
                      <div className="flex-1 text-sm"><p className="font-semibold">{it.title}</p><p className="text-xs text-muted-foreground">Qty {it.quantity}</p></div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {order.total != null && <p className="text-right text-sm font-bold mt-4">Total: {money(order.total, order.currency)}</p>}
          </div>
        )}

        <p className="mt-6 text-center text-sm text-muted-foreground">
          Have an account? <Link to="/account" className="font-bold text-taven-gold hover:underline">Sign in</Link> to view all your orders and tracking in one place.
        </p>
      </section>
    </div>
  );
}
